import { roundToNearest, USER_ROUNDING_NUM_DECIMALS } from '@common/neo.tax';

import { RdProject, type RdExpenseProjectAllocation } from '@neo.tax/rd/hooks';
import { RdAllocationsInPeriod, RdProjectAllocation } from '@neo.tax/rd/types';

export const getInitialModalAllocationPercents = (
  rdExpenseProjectAllocations: RdExpenseProjectAllocation[],
  selectedRdProject?: RdProject,
): RdAllocationsInPeriod => {
  const projectIdToProjectAllocations = new Map<string, RdProjectAllocation>();
  let nonProjectPercent = 100;

  for (const projectAllocation of rdExpenseProjectAllocations) {
    const allocationPercent = roundToNearest(projectAllocation.allocationPercentNotRounded, USER_ROUNDING_NUM_DECIMALS);
    nonProjectPercent -= allocationPercent;

    const rdProjectAllocation: RdProjectAllocation = {
      projectId: projectAllocation.rdProjectId,
      projectName: projectAllocation.project.name.trim(),
      allocationPercent,
      originalAllocationPercent: allocationPercent,
      lastAllocationPercentOnProjectPercentUpdate: allocationPercent,
      consideredForRd: projectAllocation.project.consideredForRd,
      isSection174Eligible: projectAllocation.project.isSection174Eligible,
    };

    projectIdToProjectAllocations.set(projectAllocation.rdProjectId, rdProjectAllocation);
  }

  // The server caps unsearched 0% allocations at 10 rows, so the project the contributor is being
  // added to can fall outside the fetched set. Seed it at 0% so it is always visible (pinned to the
  // top by getVisibleProjectAllocations) and editable without forcing the user to search for it.
  if (selectedRdProject && !projectIdToProjectAllocations.has(selectedRdProject.id)) {
    projectIdToProjectAllocations.set(selectedRdProject.id, {
      projectId: selectedRdProject.id,
      projectName: selectedRdProject.name.trim(),
      allocationPercent: 0,
      originalAllocationPercent: 0,
      lastAllocationPercentOnProjectPercentUpdate: 0,
      consideredForRd: selectedRdProject.consideredForRd,
      isSection174Eligible: selectedRdProject.isSection174Eligible,
    });
  }

  return {
    nonProjectPercent: {
      allocationPercent: nonProjectPercent,
      originalAllocationPercent: nonProjectPercent,
      lastAllocationPercentOnProjectPercentUpdate: nonProjectPercent,
    },
    projectPercents: {
      projectIdToProjectAllocations,
      selectedIdToProjectAllocations: new Map(),
    },
  };
};

/**
 * Derives the project rows shown in the allocation modal: every project in the (keyword-filtered)
 * result set plus the project the contributor is being added to, with that project pinned to the top.
 */
export const getVisibleProjectAllocations = (
  projectIdToProjectAllocations: Map<string, RdProjectAllocation>,
  filteredProjectIds: Set<string>,
  selectedRdProjectId?: string,
): RdProjectAllocation[] =>
  [...projectIdToProjectAllocations.values()]
    .filter(
      (projectAllocation) =>
        filteredProjectIds.has(projectAllocation.projectId) || projectAllocation.projectId === selectedRdProjectId,
    )
    .sort((a, b) => {
      if (a.projectId === selectedRdProjectId) return -1;
      if (b.projectId === selectedRdProjectId) return 1;
      return 0;
    });
